import { useState, useCallback, useEffect } from "react";
import { initializeSocket } from "@/utils/socket";

const STUDENT_ALLOWED_TYPES = ["SELECTION", "INTERVIEW_SELECTION", "JOB_REJECTED", "JOB_SHORTLISTED"];

export const useNotifications = (userId, userRole) => {
  const [notificationCount, setNotificationCount] = useState(0);
  const [recentJobs, setRecentJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  const [selectionNotifications, setSelectionNotifications] = useState([]);
  const [shortlistNotifications, setShortlistNotifications] = useState([]);

  const [selectionUnreadCount, setSelectionUnreadCount] = useState(0);
  const [shortlistUnreadCount, setShortlistUnreadCount] = useState(0);

  const API_URL = process.env.NEXT_PUBLIC_API_URL;

  const fetchNotificationCount = useCallback(async () => {
    if (!userId || userRole !== "student") return;

    try {
      const response = await fetch(
        `${API_URL}/student/${userId}/job-posts/count`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setNotificationCount(data.count || 0);
      }
    } catch (error) {
      console.error("Error fetching job post count:", error);
    }
  }, [userId, userRole, API_URL]);

  const fetchRecentJobs = useCallback(async () => {
    if (!userId || userRole !== "student") return;

    try {
      setIsLoadingJobs(true);

      const response = await fetch(`${API_URL}/${userId}/jobs-recent`, {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        const result = Array.isArray(data) ? data : data.data || [];
        setRecentJobs(result.slice(0, 4));
      } else {
        setRecentJobs([]);
      }
    } catch (error) {
      console.error("Error fetching recent jobs:", error);
      setRecentJobs([]);
    } finally {
      setIsLoadingJobs(false);
    }
  }, [userId, userRole, API_URL]);

  const fetchSelectionNotifications = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await fetch(
        `${API_URL}/notifications?page=1&limit=50`,
        { method: "GET", credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        const all = data.data || [];

        if (userRole === "student") {
          const studentScopedNotifications = all.filter((n) =>
            STUDENT_ALLOWED_TYPES.includes(n.type)
          );

          const selection = studentScopedNotifications.filter((n) =>
            ["SELECTION", "INTERVIEW_SELECTION", "JOB_REJECTED"].includes(n.type)
          );

          const shortlisted = studentScopedNotifications.filter(
            (n) => n.type === "JOB_SHORTLISTED"
          );

          setSelectionNotifications(selection);
          setShortlistNotifications(shortlisted);

          setSelectionUnreadCount(selection.filter((n) => !n.isRead).length);
          setShortlistUnreadCount(shortlisted.filter((n) => !n.isRead).length);
        } else if (userRole === "hr") {
          setSelectionNotifications(all);
          setShortlistNotifications([]);

          setSelectionUnreadCount(all.filter((n) => !n.isRead).length);
          setShortlistUnreadCount(0);
        } else if (userRole === "instructor") {
          setSelectionNotifications(all);
          setShortlistNotifications([]);

          setSelectionUnreadCount(all.filter((n) => !n.isRead).length);
          setShortlistUnreadCount(0);
        }
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [userId, userRole, API_URL]);

  const markSelectionAsRead = useCallback(
    async (notificationId) => {
      try {
        await fetch(`${API_URL}/notifications/${notificationId}/read`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        setSelectionNotifications((prev) =>
          prev.map((n) =>
            n._id === notificationId ? { ...n, isRead: true } : n
          )
        );

        setSelectionUnreadCount((prev) => Math.max(prev - 1, 0));
      } catch (err) {
        console.error("Error marking selection read:", err);
      }
    },
    [API_URL]
  );

  const markShortlistAsRead = useCallback(
    async (notificationId) => {
      try {
        await fetch(`${API_URL}/notifications/${notificationId}/read`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        setShortlistNotifications((prev) =>
          prev.map((n) =>
            n._id === notificationId ? { ...n, isRead: true } : n
          )
        );

        setShortlistUnreadCount((prev) => Math.max(prev - 1, 0));
      } catch (err) {
        console.error("Error marking shortlist read:", err);
      }
    },
    [API_URL]
  );

  const markAsViewed = useCallback(async () => {
    try {
      await fetch(`${API_URL}/student/${userId}/job-posts/viewed`, {
        method: "POST",
        credentials: "include",
      });

      setNotificationCount(0);
    } catch (error) {
      console.error("Error marking job posts viewed:", error);
    }
  }, [userId, API_URL]);

  useEffect(() => {
    if (!userId) return;

    const socket = initializeSocket(userId);

    socket.on("new-notification", (notification) => {
      if (
        userRole === "student" &&
        !STUDENT_ALLOWED_TYPES.includes(notification?.type)
      ) {
        return;
      }

      if (notification.type === "JOB_SHORTLISTED") {
        setShortlistNotifications((prev) => [notification, ...prev]);
        setShortlistUnreadCount((prev) => prev + 1);
      } else {
        setSelectionNotifications((prev) => [notification, ...prev]);
        setSelectionUnreadCount((prev) => prev + 1);
      }

      if (Notification.permission === "granted") {
        new Notification(notification.title, {
          body: notification.message,
          icon: "/logo.png",
        });
      }
    });

    return () => {
      socket.off("new-notification");
    };
  }, [userId, userRole]);

  useEffect(() => {
    if (userId) {
      if (userRole === "student") {
        fetchNotificationCount();
      }
      fetchSelectionNotifications();
    }
  }, [userId, userRole, fetchNotificationCount, fetchSelectionNotifications]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (Notification.permission === "default") {
        Notification.requestPermission();
      }
    }
  }, []);

  return {
    notificationCount,
    recentJobs,
    isLoadingJobs,
    fetchRecentJobs,
    markAsViewed,
    setNotificationCount,

    selectionNotifications,
    shortlistNotifications,
    selectionUnreadCount,
    shortlistUnreadCount,

    markSelectionAsRead,
    markShortlistAsRead,

    setSelectionNotifications,
    setShortlistNotifications,
  };
};
